import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useTranslation } from '@tyro/i18n';
import { useState } from 'react';
import type { ReturnTypeFromUseDocumentationSpaces } from '../../api/spaces';
import { CategoryCard } from './category-card';

interface ReaderCategoriesSectionProps {
  spaces: ReturnTypeFromUseDocumentationSpaces[];
  collapsedLimit: number;
  onSelectSpace: (spaceId: number) => void;
}

export function ReaderCategoriesSection({
  spaces,
  collapsedLimit,
  onSelectSpace,
}: ReaderCategoriesSectionProps) {
  const { t } = useTranslation(['documentation']);
  const [showAll, setShowAll] = useState(false);
  const canCollapse = spaces.length > collapsedLimit;
  const visibleSpaces =
    canCollapse && !showAll ? spaces.slice(0, collapsedLimit) : spaces;

  return (
    <Stack gap={1.5}>
      <Typography
        variant="caption"
        sx={{
          fontWeight: 700,
          color: 'text.secondary',
          textTransform: 'uppercase',
          letterSpacing: 0.6,
          px: 0.5,
        }}
      >
        {t('documentation:reader.browseByCategory')}
      </Typography>
      {/* Container-driven grid: the reader is a resizable FloatingCard, so
          columns must reflow on the card's own width (not the viewport).
          `auto-fill` + `minmax` adds/removes columns as the card is resized,
          with no JS. */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
          gap: 1.5,
        }}
      >
        {visibleSpaces.map((space) => (
          <CategoryCard key={space.id} space={space} onSelect={onSelectSpace} />
        ))}
      </Box>
      {canCollapse && (
        <Box sx={{ pl: 0.5 }}>
          <Link
            component="button"
            type="button"
            variant="caption"
            underline="hover"
            color="primary"
            onClick={() => setShowAll((value) => !value)}
            sx={{ fontWeight: 600 }}
          >
            {showAll
              ? t('documentation:reader.showLess')
              : t('documentation:reader.showMore', {
                  count: spaces.length - collapsedLimit,
                })}
          </Link>
        </Box>
      )}
    </Stack>
  );
}
